import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import {
  getTournamentQueryOptions,
  PlanRequiredError,
} from '@/features/tournaments/api/get-tournament';
import { hasTenant, leaveForMarketingSite } from '@/utils/tenant';

import { courtsLoader as commentatorsCourtsLoader } from './app/commentators/courts';
import { lineupsLoader } from './app/commentators/lineups';
import { courtLoader } from './app/courts/court';
import { courtsLoader } from './app/courts/courts';
import { RouteError } from './route-error';

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const tournamentLoader = (queryClient: QueryClient) => async () => {
  if (!hasTenant(window.location.hostname)) {
    leaveForMarketingSite();
    return null;
  }

  const query = getTournamentQueryOptions();

  try {
    return (
      queryClient.getQueryData(query.queryKey) ??
      (await queryClient.fetchQuery(query))
    );
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;

    if (status === 404) {
      leaveForMarketingSite();
      return null;
    }

    if (status === 403) {
      throw new PlanRequiredError();
    }

    throw error;
  }
};

export const createRouter = (queryClient: QueryClient) => {
  return sentryCreateBrowserRouter([
    {
      path: '',
      children: [
        { index: true, element: <Navigate to="/courts" /> },
        {
          path: '/commentators',
          lazy: async () => {
            const { CommentatorsPage } = await import(
              './app/commentators/commentators'
            );
            return { Component: CommentatorsPage };
          },
        },
        {
          path: '/commentators/divisions/:divisionId',
          lazy: async () => {
            const { CommentatorsDivisionsPage } = await import(
              './app/commentators/divisions'
            );
            return { Component: CommentatorsDivisionsPage };
          },
        },
        {
          path: '/commentators/divisions/:divisionId/teams/:teamId',
          lazy: async () => {
            const { CommentatorsTeamPage } = await import(
              './app/commentators/team'
            );
            return { Component: CommentatorsTeamPage };
          },
        },
        {
          path: '/commentators/courts',
          lazy: async () => {
            const { CommentatorsCourtsPage } = await import(
              './app/commentators/courts'
            );
            return { Component: CommentatorsCourtsPage };
          },
          loader: commentatorsCourtsLoader(queryClient),
        },
        {
          path: '/commentators/courts/:courtId/lineups',
          lazy: async () => {
            const { CommentatorsLineupsPage } = await import(
              './app/commentators/lineups'
            );
            return { Component: CommentatorsLineupsPage };
          },
          loader: lineupsLoader(queryClient),
        },
        {
          path: '/courts',
          lazy: async () => {
            const { CourtsPage } = await import('./app/courts/courts');
            return { Component: CourtsPage };
          },
          loader: courtsLoader(queryClient),
        },
        {
          path: '/courts/:courtId',
          lazy: async () => {
            const { CourtPage } = await import('./app/courts/court');
            return { Component: CourtPage };
          },
          loader: courtLoader(queryClient),
        },
        {
          path: '/courts/:courtId/control',
          lazy: async () => {
            const { CourtControlPage } = await import('./app/courts/control');
            return { Component: CourtControlPage };
          },
          loader: courtLoader(queryClient),
        },
        {
          path: '/courts/:courtId/tv',
          lazy: async () => {
            const { StreamOverlayPage } = await import(
              './app/courts/stream-overlay'
            );
            return { Component: StreamOverlayPage };
          },
          loader: courtLoader(queryClient),
        },
        {
          path: '*',
          lazy: async () => {
            const { NotFoundRoute } = await import('./not-found');
            return { Component: NotFoundRoute };
          },
        },
      ],
      loader: tournamentLoader(queryClient),
      errorElement: <RouteError />,
    },
  ]);
};
